import { useRef } from 'react';
import moment from 'moment';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Page } from '../components/page/Page';
import { ActionMenu, IActionMenuAction } from '../components/action-menu/ActionMenu';
import { DeleteIcon, EditIcon } from '../components/icons/Icons';
import { AlertPresenter } from '../app/AlertBinder';
import { HistoryViewModel } from '../app/HistoryViewModel';
import { useAsyncEffect } from '../support/hooks/useAsyncEffect';
import { AircraftForm } from './AircraftForm';
import { AircraftFormState } from './AircraftFormState';
import { FleetManagementState } from './FleetManagementState';
import { LabeledValue } from '../components/field/LabeledValue';
import { Card } from '../components/card/Card';
import { CardHeader } from '../components/card/CardHeader';
import { CardContent } from '../components/card/CardContent';
import { AIRCRAFT_TYPE_LABELS, AIRCRAFT_STATUS_LABELS, AIRCRAFT_WEIGHT_LABELS } from './labels';
import { formatDollars, formatDuration, formatLocalDateAndTime } from '../support/helpers/format';
import { DefaultAircraftFooter } from './DefaultAircraftFooter';
import { AircraftStatusBadge } from './AurcraftStatusBadge';
import { AircraftStats } from './AircraftStats';
import { EditableMissionState } from '../mission/model/EditableMissionState';
import { AircraftLogCard } from './AircraftLogCard';
import { RevenueCatModel } from '../paywall/revenue-cat/RevenueCatModel';
import { usePageView } from '../support/tracking/usePageView';

const formatMissionTime = (mission: EditableMissionState) => {
    return mission ? formatLocalDateAndTime({ location: mission.getLocation(), time: mission.getStartTime() }) : '';
};

export const AircraftDetailsPage: React.FC<{aircraftId: string}> = ({ aircraftId }) => {
    usePageView('Equipment Detail', () => ({ equipmentType: 'Drone' }));

    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);

    const fleetState = useInstance(FleetManagementState);
    useSubscription(() => fleetState);

    const formState = useInstance(AircraftFormState);
    useSubscription(() => formState);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    // aircraft could be undefined if user just deleted it
    const aircraft = fleetState.getAircraft(aircraftId);

    const formRef = useRef<HTMLFormElement>(null);

    useAsyncEffect(async () => {
        if (aircraft) {
            fleetState.startViewingAircraft(aircraft);
        }
    }, []);

    const onSave = async () => {
        try {
            if (formRef.current?.reportValidity()) {
                await formState.save();
            }
        } catch(e: any) {
            alert.showAndLogError(e);
        }
    };

    const onDelete = async () => {
        if (aircraft!.missions.length > 0) {
            await alert.showAlertV2({
                header: 'Delete Not Allowed',
                message: 'This drone is assigned to missions. You can update the status to Out of Service or remove it from those missions.'
            });
            return;
        }

        const destroyOrCancel = await alert.confirmDestructiveAction({
            header: 'Delete Drone?',
            message: 'This drone will be deleted and cannot be recovered.'
        });

        if (destroyOrCancel === 'destroy') {
            try {
                await formState.delete();
                history.back('/fleet');
            } catch(e: any) {
                alert.showAndLogError(e);
            }
        }
    };

    const onBack = () => {
        if (formState.isEditing) {
            formState.cancel();
        } else {
            history.back('/fleet');
        }
    };

    const onAction = async () => {
        if (formState.isEditing) {
            await onSave();
        } else {
            formState.openMenu();
        }
    };

    const menuActions: IActionMenuAction[] = [
        {
            text: 'Edit Drone',
            icon: EditIcon.raw,
            handler: formState.edit,
        }
    ];
    if (formState.canDelete()) {
        menuActions.push({
            text: 'Delete Drone',
            icon: DeleteIcon.raw,
            handler: onDelete
        });
    }

    const completedMissions = aircraft ? fleetState.getCompletedMissions(aircraft) : [];
    const lastMission = completedMissions.sort((l, r) => r.getEndTime().localeCompare(l.getEndTime()))[0];

    const plannedMissions = aircraft ? fleetState.getPlannedMissions(aircraft) : [];
    const plannedFlightMinutes = plannedMissions.reduce((minutes, mission) => minutes + mission.getDurationMinutes(), 0);
    const nextMission = plannedMissions.sort((l, r) => l.getStartTime().localeCompare(r.getStartTime()))[0];

    return (
        <Page title="Drone Details" onBack={onBack} backLabel={formState.isEditing ? 'Cancel' : undefined} onAction={onAction} actionLabel={formState.isEditing ? 'Save' : undefined} >

            {aircraft &&
                <>
                    {formState.isEditing &&
                            <AircraftForm formRef={formRef} />
                    }

                    {!formState.isEditing &&
                        <>
                            <Card>
                                <CardContent>
                                    <div style={{fontSize: 24, fontWeight: 'bold', textAlign: 'center', marginTop: -10}}>
                                        {formState.form.nickname}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 10 }}>
                                        <AircraftStatusBadge status={formState.form.status!} />
                                    </div>
                                    <div style={{marginTop: 10, marginBottom: -10}}>
                                        <AircraftStats aircraft={aircraft} paywallTrigger="equipment detail" />
                                    </div>
                                </CardContent>
                                <DefaultAircraftFooter aircraft={aircraft} onChange={fleetState.updateAircraft} trackingFrom="equipment detail" />
                            </Card>

                            <AircraftLogCard aircraftId={aircraftId} />

                            <Card>
                                <CardHeader>
                                    <span style={{fontSize: 16, fontWeight: 'bold'}}>Drone Details</span>
                                </CardHeader>
                                <CardContent>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
                                        <LabeledValue label="Drone Type" value={AIRCRAFT_TYPE_LABELS[formState.form.frameType!]} />
                                        <LabeledValue label="Drone Status" value={AIRCRAFT_STATUS_LABELS[formState.form.status!]} />
                                        <LabeledValue label="Drone Make" value={formState.form.make} />
                                        <LabeledValue label="Drone Model" value={formState.form.model} />
                                        <LabeledValue label="Serial Number" value={formState.form.serial} />
                                        <LabeledValue label="Drone Weight" value={AIRCRAFT_WEIGHT_LABELS[formState.form.weight!]} />
                                        <LabeledValue label="Drone Value" value={formatDollars(formState.form.value)} />
                                    </div>
                                </CardContent>
                            </Card>


                            {(formState.form.faaRegistrationNumber || formState.form.faaRegistrationDate) &&
                                <Card>
                                    <CardHeader>
                                        <span style={{fontSize: 16, fontWeight: 'bold'}}>FAA Details</span>
                                    </CardHeader>
                                    <CardContent>
                                        <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
                                            <LabeledValue label="Registration Number" value={formState.form.faaRegistrationNumber} />
                                            <LabeledValue label="Date Registered" value={moment(formState.form.faaRegistrationDate).format('MM/DD/YYYY')} />
                                        </div>
                                    </CardContent>
                                </Card>
                            }

                            {revenueCat.hasUpgraded() && (nextMission || lastMission) &&
                                <Card>
                                    <CardHeader>
                                        <span style={{fontSize: 16, fontWeight: 'bold'}}>Drone Use</span>
                                    </CardHeader>
                                    <CardContent>
                                        <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
                                            <LabeledValue label="Next Mission" value={formatMissionTime(nextMission)} />
                                            <LabeledValue label="Last Mission" value={formatMissionTime(lastMission)} />
                                            <LabeledValue label="Upcoming Airtime" value={formatDuration(plannedFlightMinutes)} />
                                        </div>
                                    </CardContent>
                                </Card>
                            }
                        </>
                    }

                    <ActionMenu trigger="action-button" isOpen={formState.isMenuOpen} onClose={() => formState.closeMenu()} actions={menuActions} />
                </>
            }
        </Page>
    );
};
